import React from 'react';

import { L10nDate } from '@travel/i18n';
import { useGetTranslation } from '@travel/traveler-core/hooks';
import { isEmptyArray } from '@travel/utils';

import Money from 'components/Money';

import { Translate } from 'core/translate';

import styles from './taxFee.module.scss';

const CHARGE_UNIT_LABELS: { [key: string]: string } = {
  PER_ROOM_PER_NIGHT: 'Traveler_Enumeration.Charge_Unit.Per_Room_Per_Night',
  PER_PERSON_PER_NIGHT: 'Traveler_Enumeration.Charge_Unit.Per_Person_Per_Night',
  PER_RESERVATION: 'Traveler_Enumeration.Charge_Unit.Per_Reservation',
};

type Tax = {
  amount?: number;
  chargeUnit?: string;
  currency?: string;
  percentage?: number;
  type?: string;
  name: string;
  ageFrom?: number;
  ageTo?: number;
  appliedFrom?: string;
  appliedTo?: string;
};

function TaxFee(props: { taxFee: Array<Tax>; isExclude?: boolean }) {
  const { taxFee, isExclude } = props;

  const getTranslation = useGetTranslation();

  const enumToString = (chargeUnit: string) => {
    if (CHARGE_UNIT_LABELS[chargeUnit]) {
      return `(${getTranslation({
        id: CHARGE_UNIT_LABELS[chargeUnit],
      })})`;
    } else {
      return `(${chargeUnit})`;
    }
  };

  if (isEmptyArray(taxFee)) {
    return null;
  }

  return (
    <div className={styles.taxFeeBox} data-testid="taxFee-wrapper">
      <div className={styles.taxFeeTitle}>
        {isExclude ? (
          <Translate id="Common_Price_Details.Taxes_Fees.Excluded" className={styles.taxTitle} />
        ) : (
          <Translate id="Common_Price_Details.Taxes_Fees.Included" className={styles.taxTitle} />
        )}

        {isExclude && '*'}
      </div>
      <div className={styles.taxFeeItemsBox}>
        {taxFee?.map(item => {
          const appliedToLabel = item.appliedTo && <L10nDate value={item.appliedTo} format="L" />;
          const appliedFromLabel = item.appliedFrom && (
            <L10nDate value={item.appliedFrom} format="L" />
          );

          return (
            <p className={styles.taxFeeItem} key={item.name}>
              <span className={styles.itemTitle}>
                {item.name}
                {/* date range */}
                {appliedFromLabel && appliedToLabel && (
                  <>
                    &nbsp;({appliedFromLabel}-{appliedToLabel})
                  </>
                )}
                {/* date range */}
                {(appliedFromLabel || appliedToLabel) && !(appliedFromLabel && appliedToLabel) && (
                  <>
                    &nbsp;(
                    {appliedFromLabel && (
                      <Translate
                        id="Common_Price_Details.Taxes_Fees.Start"
                        data={{ effective_date: appliedFromLabel }}
                      />
                    )}
                    {appliedToLabel && (
                      <Translate
                        id="Common_Price_Details.Taxes_Fees.Until"
                        data={{ expired_date: appliedToLabel }}
                      />
                    )}
                    )
                  </>
                )}
                {/* age range */}
                {(item.ageFrom || item.ageFrom === 0) && item.ageTo && (
                  <>
                    &nbsp;
                    {'('}
                    <Translate
                      id="Common_Price_Details.Taxes_Fees.Age"
                      data={{ minimum_age: item.ageFrom, maximum_age: item.ageTo }}
                    />
                    {')'}
                  </>
                )}
                {item.chargeUnit && <>&nbsp;{enumToString(item.chargeUnit)}</>}
              </span>
              <span>
                {item.percentage ? (
                  ` ${item.percentage}%`
                ) : (
                  <Money currency={item.currency} value={item.amount} />
                )}
              </span>
            </p>
          );
        })}
      </div>
    </div>
  );
}

export default TaxFee;
